/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {getSetting, nullify, PdfFont, RenderFile, ResponseData, showDialog, validateEmail} from '@dvrd/dvr-controls';
import {CompanyErrors, PersonalErrors, ReferralErrors, SettingErrors} from "../../settings/common/userErrorsInterface";
import {AppContext} from "../../misc/appContext";
import User, {UserFields} from "../../../models/userModel";
import Account, {AccountFields} from "../../../models/accountModel";
import {ActivateHandle, CreateAccountStep, ReferralType} from "../../../util/interfaces";
import {createErrorMessage} from "../../../util/utils";
import {DEFAULT_MAIL_TEMPLATE} from "../../../util/defaults";
import PDFTemplate from "../../../models/pdfTemplateModel";
import {getDefaultPDFItems} from "../../../util/pdfUtil";
import {onLogout} from "../../../util/events";
import CreateAccount from "./createAccount";
import {SettingsFields} from "../../../models/settingsModel";
import ExistingUserSubscriptionController from "../../subscription/existingUser/existingUserSubscriptionController";
import {useNavigate} from 'react-router';
import {INVOICES_ROUTE} from "../../../util/constants";

const USER_ERRORS: PersonalErrors = {
    zipCode: null,
    phone: null,
    lastname: null,
    firstname: null,
    email: null,
    city: null,
    address: null
};
const ACCOUNT_ERRORS: CompanyErrors = {
    phone: null,
    zipCode: null,
    email: null,
    companyName: null,
    city: null,
    address: null,
};
const SETTING_ERRORS: SettingErrors = {invoicePrefix: null, invoiceStart: null};

const REFERRAL_ERRORS: ReferralErrors = {referralOther: null, referralType: null};

export default function CreateAccountController() {
    const context = useContext(AppContext);
    const isFirstAccount = useRef<boolean | null>(context.accountContext.account === null);
    const logo = useRef<File | null>(null);
    const [user, setUser] = useState(context.userContext.user?.clone() ?? new User());
    const [account, setAccount] = useState(Account.fromUser(user));
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(isFirstAccount.current ?
        CreateAccountStep.WELCOME : CreateAccountStep.COMPANY);
    const [userErrors, setUserErrors] = useState(USER_ERRORS);
    const [accountErrors, setAccountErrors] = useState(ACCOUNT_ERRORS);
    const [settingErrors, setSettingErrors] = useState(SETTING_ERRORS);
    const [referralErrors, setReferralErrors] = useState(REFERRAL_ERRORS);
    const [logoData, setLogoData] = useState<RenderFile | null>(null);
    const [invoicePreview, setInvoicePreview] = useState('0001');
    const [referralType, setReferralType] = useState<ReferralType | null>(null);
    const [referralOther, setReferralOther] = useState('');
    const createExistingRef = useRef<ActivateHandle>(null);
    const navigate = useNavigate();

    function onChangeUser(key: keyof UserFields) {
        return function(value: string) {
            const usr = user.clone();
            usr.setField(key, value);
            setUser(usr);
            onChangeAccount(key as keyof AccountFields)(value);
        }
    }

    function onChangeAccount(key: keyof AccountFields) {
        return function(value: string) {
            if (key === 'vat_number') value = value.replace(/[ .]/g, '');
            const acc = account.clone();
            acc.setField(key, value);
            setAccount(acc);
        }
    }

    function onChangeSetting(key: keyof SettingsFields) {
        return function(value: string) {
            const acc = account.clone();
            acc.settings.setField(key, value);
            setAccount(acc);
            if (key === 'invoice_start') setInvoicePreview(value);
        }
    }

    function onChangeReferralType(type: ReferralType) {
        return function() {
            setReferralType(type);
            setReferralOther('');
            setReferralErrors({referralType: null, referralOther: null});
        }
    }

    function onChangeReferralOther(value: string) {
        setReferralOther(value);
    }

    function onStepBack() {
        let nextStep: CreateAccountStep | null = null;
        if (step === CreateAccountStep.FINISHED) nextStep = CreateAccountStep.INVOICE;
        else if (step === CreateAccountStep.INVOICE) nextStep = CreateAccountStep.ADDITIONAL;
        else if (step === CreateAccountStep.ADDITIONAL) nextStep = CreateAccountStep.COMPANY;
        else if (step === CreateAccountStep.COMPANY) nextStep = CreateAccountStep.PERSONAL;
        else if (step === CreateAccountStep.REFERRAL) nextStep = CreateAccountStep.INVOICE;
        if (nextStep) setStep(nextStep);
    }

    function onStepForward() {
        if (!validateStep()) return;
        let nextStep: CreateAccountStep | null = null;
        if (step === CreateAccountStep.WELCOME) nextStep = CreateAccountStep.PERSONAL;
        else if (step === CreateAccountStep.PERSONAL) nextStep = CreateAccountStep.COMPANY;
        else if (step === CreateAccountStep.COMPANY) nextStep = CreateAccountStep.ADDITIONAL;
        else if (step === CreateAccountStep.ADDITIONAL) nextStep = CreateAccountStep.INVOICE;
        else if (step === CreateAccountStep.INVOICE) {
            if (user.isNewUser) nextStep = CreateAccountStep.REFERRAL;
            else return onSubmit();
        } else if (step === CreateAccountStep.REFERRAL) return onSubmit();
        else if (step === CreateAccountStep.FINISHED) navigate(INVOICES_ROUTE);
        if (nextStep) setStep(nextStep);
    }

    function onSubmit() {
        if (!validateAll()) return;
        setLoading(true);
        onSubmitUser();
    }

    function onSubmitUser() {
        const _user = user.clone();
        if (!_user.flex)
            _user.flex = {};
        _user.setFlex('referral_type', referralType);
        _user.setFlex('referral_other', nullify(referralOther));
        _user.setFlex('registration_complete', true);
        const updateBilling = !_user.accounts.length;
        _user.update(undefined, (user: User, success: boolean, data: ResponseData) => {
            if (success) context.onChangeContext({user}, onSubmitAccount(user));
            else {
                setLoading(false);
                showDialog(data.message || createErrorMessage('Het instellen van je account is niet gelukt.'),
                    'Instellen mislukt');
            }
        }, updateBilling)
    }

    function onSubmitAccount(user: User) {
        // We pass the user as param to prevent using outdated context data
        return function() {
            account.settings.emailTemplate = DEFAULT_MAIL_TEMPLATE;
            account.flex = {'use_no_reply_tracking': true};
            account.save(undefined, undefined, logo.current,
                (account: Account, success: boolean, data: ResponseData) => {
                    if (success) {
                        user.accounts.push(account);
                        context.onChangeContext({user: user, accounts: user.accounts, account},
                            onSubmitTemplate(account));
                    } else {
                        setLoading(false);
                        showDialog(data.message || createErrorMessage('Het instellen van je account is niet gelukt.'),
                            'Instellen mislukt');
                    }
                });
        }
    }

    function onSubmitTemplate(account: Account) {
        return function() {
            const template = new PDFTemplate({account_id: account.id, main_font: PdfFont.LATO});
            template.save(getDefaultPDFItems(account,
                    account.logo ? getSetting('filePrefix') + account.logo : undefined),
                (template: PDFTemplate, success: boolean, data: ResponseData) => {
                    setLoading(false);
                    if (success) {
                        account.template = template;
                        context.onChangeContext({account}, () => {
                            setStep(CreateAccountStep.FINISHED);
                        });
                    } else showDialog(
                        data.message || createErrorMessage('Het instellen van je account is niet gelukt.'),
                        'Instellen mislukt');
                });
        }
    }

    function onSignOut() {
        onLogout();
    }

    function onResetUserError(key: keyof PersonalErrors) {
        return function() {
            setUserErrors({...userErrors, [key]: null});
        }
    }

    function onResetAccountError(key: keyof CompanyErrors) {
        return function() {
            setAccountErrors({...accountErrors, [key]: null});
        }
    }

    function onResetSettingError(key: keyof SettingErrors) {
        return function() {
            setSettingErrors({...settingErrors, [key]: null});
        }
    }

    function onResetReferralError(key: keyof ReferralErrors) {
        return function() {
            setReferralErrors({...referralErrors, [key]: null});
        }
    }

    function onChangeLogo(file: File) {
        if (/^image\/(PNG|JPEG|JPG)$/i.test(file.type)) {
            logo.current = file;
            setLogoData({filename: file.name, src: URL.createObjectURL(file)});
        } else showDialog('Alleen PNG, JPG en JPEG afbeeldingen zijn toegestaan.');
    }

    function onChangeInvoiceNum(value: string) {
        setInvoicePreview(value);
    }

    function onCancel() {
        navigate('/facturen');
    }

    function validateAll(): boolean {
        if (!validatePersonalStep()) {
            setStep(CreateAccountStep.PERSONAL);
            return false;
        } else if (!validateCompanyStep()) {
            setStep(CreateAccountStep.COMPANY);
            return false;
        } else if (!validateInvoiceStep()) {
            setStep(CreateAccountStep.INVOICE);
            return false;
        } else if (user.isNewUser && !validateReferralStep()) {
            setStep(CreateAccountStep.REFERRAL);
            return false;
        }
        return true;
    }

    function validateStep(): boolean {
        switch (step) {
            case CreateAccountStep.PERSONAL:
                return validatePersonalStep();
            case CreateAccountStep.COMPANY:
                return validateCompanyStep();
            case CreateAccountStep.INVOICE:
                return validateInvoiceStep();
            case CreateAccountStep.REFERRAL:
                return validateReferralStep();
        }
        return true;
    }

    function validatePersonalStep(): boolean {
        const errors = Object.assign({}, userErrors);
        let valid: boolean = true;
        if (!user.firstname) {
            errors.firstname = 'Vul je voornaam in';
            valid = false;
        }
        if (!user.lastname) {
            errors.lastname = 'Vul je achternaam in';
            valid = false;
        }
        if (!user.email) {
            errors.email = 'Vul je e-mailadres in';
            valid = false;
        } else if (!validateEmail(user.email)) {
            errors.email = 'Vul een geldig e-mailadres in';
            valid = false;
        }
        if (!user.telNumber) {
            errors.phone = 'Vul je telefoonnummer in';
            valid = false;
        }
        if (!user.address) {
            errors.address = 'Vul je adres in';
            valid = false;
        }
        if (!user.zipCode) {
            errors.zipCode = 'Vul je postcode in';
            valid = false;
        }
        if (!user.city) {
            errors.city = 'Vul je stad in';
            valid = false;
        }
        if (!valid) setUserErrors(errors);
        return valid;
    }

    function validateCompanyStep(): boolean {
        const errors = Object.assign({}, accountErrors);
        let valid: boolean = true;
        if (!account.companyName) {
            errors.companyName = 'Vul de bedrijfsnaam in';
            valid = false;
        }
        if (!account.email) {
            errors.email = 'Vul het e-mailadres in';
            valid = false;
        } else if (!validateEmail(account.email)) {
            errors.email = 'Vul een geldig e-mailadres in';
            valid = false;
        }
        if (!account.telNumber) {
            errors.phone = 'Vul het telefoonnummer in';
            valid = false;
        }
        if (!account.address) {
            errors.address = 'Vul het adres in';
            valid = false;
        }
        if (!account.zipCode) {
            errors.zipCode = 'Vul de postcode in';
            valid = false;
        }
        if (!account.city) {
            errors.city = 'Vul de stad in';
            valid = false;
        }
        if (!valid) setAccountErrors(errors);
        return valid;
    }

    function validateInvoiceStep(): boolean {
        const errors = Object.assign({}, settingErrors), {settings} = account;
        let valid: boolean = true;
        if (!settings.invoicePrefix) {
            errors.invoicePrefix = 'Vul de voortekens in';
            valid = false;
        }
        if (!settings.invoiceStart) {
            errors.invoiceStart = 'Vul het startgetal in';
            valid = false;
        }
        if (!valid) setSettingErrors(errors);
        return valid;
    }

    function validateReferralStep(): boolean {
        const errors = {...referralErrors};
        let valid: boolean = true;
        if (referralType === ReferralType.OTHER && !nullify(referralOther)) {
            errors.referralOther = 'Dit veld is verplicht';
            valid = false;
        }
        if (!valid) setReferralErrors(errors);
        return valid;
    }

    function checkSubscription() {
        const {user} = context.userContext;
        if (!user) return;
        setLoading(true);
        user.hasSubscription((hasSubscription: boolean, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                if (!hasSubscription) createExistingRef.current?.activate();
            } else
                showDialog(createErrorMessage(data.message ?? 'Het is niet gelukt om te controleren of je een actief ' +
                    'abonnement hebt.'));
        });
    }

    useEffect(() => {
        checkSubscription();
    }, []);

    return (
        <>
            <CreateAccount onChangeUser={onChangeUser} onChangeAccount={onChangeAccount}
                           onChangeSetting={onChangeSetting} onStepBack={onStepBack} onStepNext={onStepForward}
                           onSignOut={onSignOut} onResetUserError={onResetUserError}
                           onResetAccountError={onResetAccountError} onResetSettingError={onResetSettingError}
                           onChangeLogo={onChangeLogo} onChangeInvoiceNum={onChangeInvoiceNum} onSubmit={onSubmit}
                           onCancel={onCancel} onChangeReferralType={onChangeReferralType}
                           onChangeReferralOther={onChangeReferralOther} onResetReferralError={onResetReferralError}
                           step={step} user={user} account={account} userErrors={userErrors}
                           accountErrors={accountErrors} settingErrors={settingErrors} logo={logoData}
                           invoicePreview={invoicePreview} loading={loading} isFirstAccount={isFirstAccount.current}
                           referralType={referralType} referralOther={referralOther} referralErrors={referralErrors}/>
            <ExistingUserSubscriptionController ref={createExistingRef}/>
        </>
    )
}